/* `GET /builder` — Definable, the workspace's own agent that builds and manages the others.
   Talking to it is `POST /runs` and its conversations are `GET /agents/{id}/threads`, like
   any agent's (Definable's list holds only yours). `backend/modules/builder/README.md` for the wire. */
import { useQuery } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type { Agent } from "@/shared/types";

export interface BuilderRow {
  agent_id: string;
  name: string;
}

export const builderKeys = { one: ["builder"] as const };

export async function getBuilder(): Promise<BuilderRow> {
  return unwrap(await client.get<Envelope<BuilderRow>>("/builder"));
}

/** The workspace's Definable agent: made on first use, so the first call creates it. */
export const useBuilder = () => useQuery({ queryKey: builderKeys.one, queryFn: getBuilder, staleTime: Infinity });

/** Definable as the shell draws an agent. Nobody edits it, so everything a settings page
    would show is empty: its chat has no model picker, and its only tabs are Chat and Threads. */
export const toDefinable = ({ agent_id, name }: BuilderRow): Agent => ({
  id: agent_id,
  name,
  definable: true,
  hue: 260,
  seed: "graph-builder",
  model: "claude-opus-5-5",
  version: "v1",
  versions: [],
  connectedApps: [],
  allApps: [],
  capabilities: { memoryEnabled: false, appsEnabled: false, sandboxEnabled: false, sandboxSize: null, webEnabled: true },
  instructions: "",
  modelSettings: { model: "claude-opus-5-5", temperature: 0.7, maxTokens: 16000 },
  needsApproval: [],
  chats: [],
});
